// localStorage side of the board filters: one versioned envelope per board, per user, per company.
//
// Scoped by BOTH, because each half answers a different failure. The company, because a
// dispatcher/driver/broker id only means something inside the company it came from — replaying one
// company's ids in another silently empties the board. The user, because a filter selection is
// personal (it can literally be "my trucks", holding that user's own id) while localStorage belongs
// to the browser: on a shared dispatch-office terminal, the next person to sign in must start from
// their own filters, not inherit whoever used it last.
//
// Nothing here knows what a filter is: it stores and returns whatever bag of values the board spec
// produced, and boardFilterSpecs validates each one on the way back in.

import type { BoardFilterSpec } from './boardFilterSpecs'
import { isRecord } from 'utils/isRecord'

/** Bump when a stored bag can no longer be read by the current field parsers. */
const STORAGE_VERSION = 1

export type StoredBoardFilters = Record<string, unknown>

/** Whose filters, in which company. Both are needed before anything can be read or written. */
export interface BoardFilterScope {
    projectName: string
    userId: string
}

interface StoredEnvelope {
    version: number
    filters: StoredBoardFilters
}

/** One string per user per company — what "whose is this" is compared by, and the tail of every storage key. */
export const scopeKey = (scope: BoardFilterScope): string => `${scope.projectName}:${scope.userId}`

export const boardFiltersStorageKey = (spec: BoardFilterSpec, scope: BoardFilterScope): string => `${spec.storageKeyPrefix}:${scopeKey(scope)}`

/** Whatever this user last saved for this board here — `{}` when there is nothing usable to restore. */
export const readStoredBoardFilters = (spec: BoardFilterSpec, scope: BoardFilterScope): StoredBoardFilters => {
    try {
        const raw = localStorage.getItem(boardFiltersStorageKey(spec, scope))
        if (!raw) return {}
        const parsed: unknown = JSON.parse(raw)
        if (!isRecord(parsed) || parsed.version !== STORAGE_VERSION) return {}
        return isRecord(parsed.filters) ? parsed.filters : {}
    } catch {
        // Unreadable storage (corrupt entry, private mode) is the same as nothing saved.
        return {}
    }
}

/**
 * Saves this user's filters for one board — or drops the entry entirely when there is nothing left
 * to remember, so a board back at its defaults stops occupying a key.
 */
export const writeStoredBoardFilters = (spec: BoardFilterSpec, scope: BoardFilterScope, filters: StoredBoardFilters): void => {
    const key = boardFiltersStorageKey(spec, scope)
    try {
        if (Object.keys(filters).length === 0) localStorage.removeItem(key)
        else localStorage.setItem(key, JSON.stringify({ version: STORAGE_VERSION, filters } satisfies StoredEnvelope))
    } catch {
        // ignore storage failures (private mode, quota, etc.)
    }
}
